import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import dragula from "dragula";
import "dragula/dist/dragula.css";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import { API_URL } from '../../environment';
import CardViewModal from "../crm/leads/CardViewModal";

interface Stage {
  id: number;
  nome_funil: string;
  ordem: number;
}

interface Lead {
  id: number;
  nome: string;
  fone: string;
  fone_original?: string;
  nome_funil: string;
  data_entrada: string;
  cnpj: string;
  razao_social: string;
  etapa_cadencia?: number | null;
  qtd_msg_texto?: number | null;
}

const KanbanView = () => {
  const routes = all_routes;
  const idUser = localStorage.getItem("id_user")
  const nomePai = localStorage.getItem("nome_pai") || "Leads";
  
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const isAdminMaster = localStorage.getItem("isAdminMaster") === "true";
  
  const [times, setTimes] = useState<{ id: string; nome: string }[]>([]);
  const [selectedTimeId, setSelectedTimeId] = useState<string>(localStorage.getItem("id_cliente") || "");
  
  const [usuarios, setUsuarios] = useState<{ id: number; nome: string }[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>(
    (!isAdmin && !isAdminMaster) ? (localStorage.getItem("id_user") || "") : ""
  );

  const [stages, setStages] = useState<Stage[]>([]);
  
  // 🆕 Leads por estágio (coluna)
  const [leadsByStage, setLeadsByStage] = useState<Record<number, Lead[]>>({});
  const [stagePagination, setStagePagination] = useState<Record<number, { page: number, hasMore: boolean, loading: boolean }>>({});

  const isFetchingStages = useRef(false);
  const containersRef = useRef<HTMLDivElement[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [cardModalShow, setCardModalShow] = useState(false);
  const [selectedLeadPhone, setSelectedLeadPhone] = useState("");
  const [selectedLeadName, setSelectedLeadName] = useState("");
  const [selectedFunnelName, setSelectedFunnelName] = useState("");

  const handleOpenCardModal = (lead: Lead) => {
    // 🧹 Prepara dados para o CardViewModal (Auto-fill consistency)
    const rawPhone = (lead.fone_original || lead.fone || "").replace(/\D/g, "");
    const formattedPhone = (rawPhone.length >= 10 && rawPhone.length <= 11) ? `55${rawPhone}` : rawPhone;
    
    setSelectedLeadPhone(formattedPhone);
    setSelectedLeadName((lead.nome || "").trim());
    setSelectedFunnelName(localStorage.getItem("nome_pai") || "");
    setCardModalShow(true);
  };

  useEffect(() => {
    if (isAdminMaster) {
      const id_grupo = localStorage.getItem("id_grupo");
      if (id_grupo) {
        fetch(`${API_URL}/pegartodostimes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_grupo }),
        })
          .then(res => res.json())
          .then(data => {
            if (Array.isArray(data)) {
              setTimes(data);
            }
          })
          .catch(console.error);
      }
    }
  }, [isAdminMaster]);

  useEffect(() => {
    if (!selectedTimeId) {
      setUsuarios([]);
      return;
    }
    fetch(`${API_URL}/pegarusuariostime`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id_time: selectedTimeId }),
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setUsuarios(data);
        else setUsuarios([]);
      })
      .catch(console.error);
  }, [selectedTimeId]);

  const fetchStages = async () => {
    if(isFetchingStages.current || !selectedTimeId) return;
    
    // 💾 Cache: Load stages immediately
    const cacheKey = `kanban_stages_${selectedTimeId}_${nomePai}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const data = JSON.parse(cached);
        if (Array.isArray(data)) setStages(data);
      } catch (e) { console.error("Cache kanban stages error", e); }
    }

    isFetchingStages.current = true;
    try {
      const response = await fetch(`${API_URL}/pegaretapas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_cliente: selectedTimeId,
          nome_pai: nomePai,
        }),
      });
  
      if (!response.ok) {
        throw new Error("Erro ao buscar etapas");
      }
  
      const data = await response.json();
      const orderedStages = data.sort((a: Stage, b: Stage) => a.ordem - b.ordem);
      setStages(orderedStages);
      localStorage.setItem(cacheKey, JSON.stringify(orderedStages)); // ✅ Update cache
    } catch (err) {
      console.error("Erro ao buscar etapas:", err);
    } finally {
      isFetchingStages.current = false;
    }
  };

  // 🆕 Função para buscar leads de uma etapa específica (Paginado)
  const fetchStageLeads = async (stageId: number, page: number = 1, reset: boolean = false) => {
    // Evita chamadas duplicadas se já estiver carregando
    if (!reset && stagePagination[stageId]?.loading) return;

    const cacheKey = `kanban_leads_${selectedTimeId}_${stageId}`;
    
    // 💾 Cache: Load leads immediately (only on first page/reset)
    if (page === 1 || reset) {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            try {
                const leads = JSON.parse(cached);
                if (Array.isArray(leads)) {
                    setLeadsByStage(prev => ({ ...prev, [stageId]: leads }));
                }
            } catch (e) { console.error("Cache kanban leads error", e); }
        }
    }

    setStagePagination(prev => ({
      ...prev,
      [stageId]: { 
        page, 
        hasMore: reset ? true : (prev[stageId]?.hasMore ?? true), 
        loading: true 
      }
    }));

    try {
      const response = await fetch(`${API_URL}/pegar_leads_coluna_kanban`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_cliente: selectedTimeId,
          id_etapa: stageId,
          filter_user_id: selectedUserId || null,
          page: page,
          pageSize: 20
        }),
      });

      if (!response.ok) throw new Error(`Erro ao buscar leads da etapa ${stageId}`);

      const data = await response.json();
      const newLeads = data.leads || [];
      const hasMore = data.hasMore || false;

      setLeadsByStage(prev => {
        // Se for reset, substitui. Se não, concatena.
        // Usa Map para dedup por fone se necessário, mas aqui confiamos na paginação
        const currentList = reset ? [] : (prev[stageId] || []);
        
        // Deduplicação básica por ID ou Fone para evitar chaves duplicadas no React
        const existingIds = new Set(currentList.map(l => l.id));
        const filteredNew = newLeads.filter((l: Lead) => !existingIds.has(l.id));
        
        const finalList = [...currentList, ...filteredNew];
        
        // ✅ Update cache (only for first page loads to avoid huge objects, 
        // or just cache the first page. Here we cache what we have visible)
        if (page === 1) { // Só cacheia a primeira página para não estourar storage
             localStorage.setItem(cacheKey, JSON.stringify(finalList));
        }
        
        return {
          ...prev,
          [stageId]: finalList
        };
      });

      setStagePagination(prev => ({
        ...prev,
        [stageId]: { page, hasMore, loading: false }
      }));

    } catch (err) {
      console.error(err);
      setStagePagination(prev => ({
        ...prev,
        [stageId]: { ...prev[stageId], loading: false }
      }));
    }
  };

  // Carrega leads iniciais para todas as etapas quando stages mudar
  useEffect(() => {
    if (stages.length > 0) {
      // Limpa leads antigos ao mudar filtro (stages recarregados)
      setLeadsByStage({});
      setStagePagination({});
      
      stages.forEach(stage => {
        fetchStageLeads(stage.id, 1, true);
      });
    }
  }, [stages, selectedUserId]); // Recarrega se usuário mudar também (filtro)

  const requestQueue = useRef<{ fone: string; novaEtapa: string; idcliente: string }[]>([]);
  const isProcessingQueue = useRef(false);

  const processQueue = async () => {
    if (isProcessingQueue.current || requestQueue.current.length === 0) return;

    isProcessingQueue.current = true;
    const { fone, novaEtapa, idcliente } = requestQueue.current[0]; // Peek

    try {
        await fetch(`${API_URL}/atualizaretapa`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ fone, nova_etapa: novaEtapa, idcliente }),
        });
    } catch (err) {
        console.error("Erro ao atualizar etapa (Background):", err);
    } finally {
        requestQueue.current.shift(); // Dequeue
        isProcessingQueue.current = false;
        if (requestQueue.current.length > 0) {
            processQueue(); // Process next
        }
    }
  };

  const updateLeadStage = (fone: string, novaEtapa: string) => {
      requestQueue.current.push({ fone, novaEtapa, idcliente: selectedTimeId });
      processQueue();
  };

  useEffect(() => {
    const containers = containersRef.current.filter(Boolean);
    const drake = dragula(containers);

    const dragState = { x: 0 as number, intervalId: 0 as number };

    const onMouseMove = (e: MouseEvent) => {
      dragState.x = e.clientX;
    };
    
    const doAutoScroll = () => {
      const scrollEl = scrollRef.current;
      if (!scrollEl) return;
      const { left, right } = scrollEl.getBoundingClientRect();
      const threshold = 80;
      const speed = 70;
  
      if (dragState.x > right - threshold) {
        scrollEl.scrollBy({ left: speed, behavior: "auto" });
      } else if (dragState.x < left + threshold) {
        scrollEl.scrollBy({ left: -speed, behavior: "auto" });
      }
    };

    drake.on("drag", () => {
      document.addEventListener("mousemove", onMouseMove);
      if (!dragState.intervalId) {
        dragState.intervalId = window.setInterval(doAutoScroll, 50);
      }
    });

    const cleanup = () => {
      document.removeEventListener("mousemove", onMouseMove);
      window.clearInterval(dragState.intervalId);
      dragState.intervalId = 0;
    };

    drake.on("drop", cleanup);
    drake.on("dragend", cleanup);
    drake.on("cancel", cleanup);

    drake.on("drop", async (el, target, source, sibling) => {
      if (!el || !target || !source) return;

      const leadFone = el.getAttribute("data-fone");
      const newStageName = target.getAttribute("data-stage");
      const sourceStageName = source.getAttribute("data-stage");

      // FIX: Revert DOM changes immediately so React can handle the update
      drake.cancel(true);

      if (leadFone && newStageName && sourceStageName) {
        const sourceStageId = stages.find(s => s.nome_funil === sourceStageName)?.id;
        const newStageId = stages.find(s => s.nome_funil === newStageName)?.id;

        if (!sourceStageId || !newStageId || sourceStageId === newStageId) {
            return;
        }

        // 🚀 Optimistic Update: Move visualmente AGORA
        setLeadsByStage(prev => {
            const sourceList = prev[sourceStageId] || [];
            const destList = prev[newStageId] || [];
            const leadIndex = sourceList.findIndex(l => (l.fone_original || l.fone) === leadFone);
            
            if (leadIndex === -1) return prev; 

            const lead = { ...sourceList[leadIndex], nome_funil: newStageName };
            const newSourceList = [...sourceList];
            newSourceList.splice(leadIndex, 1);
            
            // Adiciona no topo da lista de destino para feedback visual imediato
            return {
                ...prev,
                [sourceStageId]: newSourceList,
                [newStageId]: [lead, ...destList] 
            };
        });

        // 🛡️ Validação no Backend
        const leadObj = (leadsByStage[sourceStageId] || []).find(l => (l.fone_original || l.fone) === leadFone);
        const phoneFull = leadObj?.fone_original || leadFone; 
        
        const currentIndex = stages.findIndex(s => s.nome_funil === sourceStageName);
        const newIndex = stages.findIndex(s => s.nome_funil === newStageName);
        let tipo = newIndex < currentIndex ? 'retrocesso' : 'avanco';

        try {
          const valRes = await fetch(`${API_URL}/card/validate_move`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id_cliente: selectedTimeId, fone: phoneFull, tipo }),
          });

          if (valRes.ok) {
            const valData = await valRes.json();
            if (!valData.allowed) {
              const actionName = tipo === 'avanco' ? "avançar" : "retroceder";
              alert(`🚫 Ação bloqueada!\n\nPara ${actionName} desta etapa, você deve preencher os seguintes campos no Card:\n\n- ${valData.missing_fields.join("\n- ")}`);
              
              // ↩️ Revert Optimistic Update (Rollback)
              setLeadsByStage(prev => {
                  const currentSourceList = prev[sourceStageId] || []; // Agora é a lista "vazia"
                  const currentDestList = prev[newStageId] || [];     // Agora tem o lead "inválido"
                  
                  // Acha o lead na lista de destino (onde o colocamos otimisticamente)
                  const leadIndex = currentDestList.findIndex(l => (l.fone_original || l.fone) === leadFone);
                  if (leadIndex === -1) return prev;

                  const lead = { ...currentDestList[leadIndex], nome_funil: sourceStageName };
                  const newDestList = [...currentDestList];
                  newDestList.splice(leadIndex, 1);

                  return {
                      ...prev,
                      [sourceStageId]: [lead, ...currentSourceList], // Devolve para a origem
                      [newStageId]: newDestList
                  };
              });
              return;
            }
          }
        } catch (e) {
          console.error("Erro validação kanban:", e);
          // Em caso de erro de rede, talvez devêssemos reverter também? 
          // Por enquanto, segue o fluxo ou mantém o estado otimista.
        }

        // ✅ Se validado, atualiza backend
        updateLeadStage(phoneFull, newStageName);
      } else {
        drake.cancel(true);
      }
    });

    return () => {
      drake.destroy();
    };
  }, [stages, leadsByStage, selectedTimeId]); // leadsByStage nas deps para drake ter acesso ao estado atualizado? Cuidado com re-init constante.
  // Melhor usar refs para acessar estado atual dentro dos callbacks do Dragula se leadsByStage muda muito.
  // Mas vamos deixar assim por enquanto.

  useEffect(() => {
    if (selectedTimeId) {
      fetchStages();
      // fetchLeads(); // Removido global fetch
    }
  }, [selectedTimeId]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);
  

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div className="d-flex align-items-center gap-3">
              <h4>Kanban {nomePai}</h4>

              {isAdminMaster && (
                <select
                  className="form-select form-select-sm"
                  style={{ maxWidth: '200px' }}
                  value={selectedTimeId}
                  onChange={(e) => setSelectedTimeId(e.target.value)}
                >
                  {times.map(t => (
                    <option key={t.id} value={t.id}>{t.nome}</option>
                  ))}
                </select>
              )}

              {(isAdmin || isAdminMaster) && usuarios.length > 0 && (
                <select
                  className="form-select form-select-sm"
                  style={{ maxWidth: '200px' }}
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  <option value="">— Todos Usuários —</option>
                  {usuarios.map(u => (
                    <option key={u.id} value={u.id}>{u.nome}</option>
                  ))}
                </select>
              )}
            </div>

            <div className="d-flex align-items-center border bg-white rounded p-1 me-2 icon-list">
              <Link to={routes.leadsList} className="btn btn-icon btn-sm me-1">
                <i className="ti ti-list-tree" />
              </Link>
              <Link
                to={routes.kanbanView}
                className="btn btn-icon btn-sm active bg-success text-white"
              >
                <i className="ti ti-layout-grid" />
              </Link>
            </div>
          </div>
          <div
            className="card-body"
            style={{
              overflow: "visible",
              paddingBottom: "0",
            }}
          >
            <div
              className="kanban-scroll-wrapper"
              style={{
                position: "relative",
                height: "calc(100vh - 240px)",
                width: "100%",
              }}
            >
              <div
                className="kanban-scroll-x"
                ref={scrollRef}
                style={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  paddingBottom: "5px",
                  width: "100%",
                }}
              >
                <div
                  className="d-flex align-items-start project-status pb-4"
                  style={{
                    minWidth: "max-content",
                    overflow: "visible",
                    gap: "16px",
                    width: "100%",
                  }}
                >
                  {stages.map((stage, index) => (
                    <div
                      key={stage.id}
                      className="p-2 rounded bg-transparent-secondary w-100"
                      style={{
                        minWidth: "260px",
                        maxWidth: "260px",
                      }}
                    >
                      <div className="bg-white p-1 rounded mb-2">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <span
                              className="bg-soft-pink p-1 d-flex rounded-circle me-1"
                              style={{ width: "18px", height: "18px" }}
                            >
                              <span
                                className="bg-purple rounded-circle d-block p-1"
                                style={{ width: "10px", height: "10px" }}
                              />
                            </span>
                            <h6
                              className="me-1"
                              style={{
                                fontSize: "12px",
                                margin: 0,
                              }}
                            >
                              {stage.nome_funil}
                            </h6>
                            <span
                              className="badge bg-light rounded-pill"
                              style={{ fontSize: "10px" }}
                            >
                              {
                                (leadsByStage[stage.id] || []).length
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                      <div
                        className="kanban-drag-wrap"
                        ref={(el) => {
                          if (el) containersRef.current[index] = el;
                        }}
                        data-stage={stage.nome_funil}
                        style={{
                          overflowY: "auto",
                          maxHeight: "calc(65vh)",
                          minHeight: "60vh",
                          paddingRight: "10px",
                        }}
                        onScroll={(e) => {
                            const target = e.currentTarget;
                            if (target.scrollHeight - target.scrollTop <= target.clientHeight + 50) {
                                // Scrolled to bottom
                                const pagination = stagePagination[stage.id];
                                if (pagination && !pagination.loading && pagination.hasMore) {
                                    fetchStageLeads(stage.id, pagination.page + 1);
                                }
                            }
                        }}
                      >
                        {(leadsByStage[stage.id] || []).map((lead) => (
                            <div
                              key={lead.id}
                              className="card kanban-card mb-2"
                              data-fone={lead.fone_original || lead.fone}
                              style={{
                                fontSize: "11px",
                                margin: "4px",
                              }}
                            >
                              <div className="card-body">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <div className="d-flex align-items-center">
                                    <span
                                      className="badge bg-outline-dark me-1"
                                      style={{ fontSize: "9px" }}
                                    >
                                      Etapa
                                    </span>
                                    <div className="d-flex align-items-center">
                                      <span
                                        className="badge bg-success badge-xs d-flex align-items-center justify-content-center"
                                        style={{
                                          fontSize: "9px",
                                          padding: "3px",
                                          minWidth: "fit-content"
                                        }}
                                      >
                                        {lead.nome_funil && lead.nome_funil.length > 15 
                                          ? lead.nome_funil.substring(0, 15) + "..." 
                                          : lead.nome_funil}
                                      </span>

                                      {Number(lead.qtd_msg_texto || 0) > 0 && (
                                        <span
                                          style={{
                                            color:
                                              lead.etapa_cadencia != null &&
                                              lead.qtd_msg_texto != null &&
                                              lead.etapa_cadencia + 1 > lead.qtd_msg_texto
                                                ? "#0a0"
                                                : "#000",
                                            fontSize: "9px",
                                            marginLeft: "4px",
                                            fontWeight: "500",
                                          }}
                                        >
                                          {lead.etapa_cadencia == null
                                            ? `1/${lead.qtd_msg_texto ?? 0}`
                                            : lead.qtd_msg_texto != null && lead.etapa_cadencia + 1 > lead.qtd_msg_texto
                                              ? "✅"
                                              : `${lead.etapa_cadencia + 1}/${lead.qtd_msg_texto ?? 0}`}
                                        </span>
                                      )}
                                    </div>
                                    
                                    <button 
                                        className="btn btn-sm btn-icon btn-light ms-auto" 
                                        onClick={() => handleOpenCardModal(lead)}
                                        title="Visualizar Cards"
                                        style={{ width: "20px", height: "20px", padding: 0 }}
                                    >
                                        <i className="ti ti-id" style={{ fontSize: "12px" }} />
                                    </button>
                                  </div>
                                </div>
                                  <div className="d-flex align-items-center mb-1">
                                    <span
                                      className="avatar avatar-xs rounded-circle bg-warning me-1"
                                      style={{ width: "14px", height: "14px" }}
                                    >
                                      <ImageWithBasePath
                                        src="assets/img/icons/kanban-arrow.svg"
                                        className="w-auto h-auto"
                                        alt="Img"
                                      />
                                    </span>
                                    <h6 className="d-flex align-items-center" style={{ fontSize: "11px" }}>
                                      <Link
                                        to={routes.chat}
                                        state={{
                                          phone: lead.fone.startsWith("55") ? lead.fone : "55" + lead.fone,
                                        }}
                                      >
                                        {lead.nome}
                                      </Link>
                                    </h6>
                                  </div>
                                <div className="d-flex align-items-center border-bottom mb-1 pb-1">
                                  <div
                                    className="me-2 pe-2 border-end"
                                    style={{ fontSize: "9px" }}
                                  >
                                    <span className="fw-medium d-block mb-1">Número</span>
                                    <p className="text-dark">{lead.fone_original || lead.fone}</p>
                                  </div>
                                  <div className="" style={{ fontSize: "9px" }}>
                                    <span className="fw-medium d-block mb-1">
                                      Data de Entrada
                                    </span>
                                    <p className="text-dark">
                                      {(() => {
                                        if (!lead.data_entrada) return "—";
                                        const d = new Date(lead.data_entrada);
                                        d.setDate(d.getDate() + 1);
                                        return new Intl.DateTimeFormat("pt-BR", {
                                          day:   "2-digit",
                                          month: "2-digit",
                                          year:  "numeric",
                                        }).format(d);
                                      })()}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          {stagePagination[stage.id]?.loading && (
                              <div className="text-center py-2">
                                <small>Carregando...</small>
                              </div>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CardViewModal 
        show={cardModalShow}
        onHide={() => setCardModalShow(false)}
        leadPhone={selectedLeadPhone}
        leadName={selectedLeadName}
        initialFunnelName={selectedFunnelName}
      />
    </div>
  );
};

export default KanbanView;
